import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CompressIcon from '@mui/icons-material/Compress';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useChat } from './ChatProvider';
import type { ChatMessage } from 'api/ChatApiWrapper';

const mdComponents = {
  table: (props: React.HTMLAttributes<HTMLTableElement>) => (
    <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem', my: 1 }} {...props} />
  ),
  thead: (props: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <Box component="thead" sx={{ bgcolor: 'grey.200' }} {...props} />
  ),
  tbody: (props: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <Box component="tbody" sx={{ '& tr:nth-of-type(even)': { bgcolor: 'grey.50' } }} {...props} />
  ),
  th: (props: React.ThHTMLAttributes<HTMLTableCellElement>) => (
    <Box component="th" sx={{ px: 1, py: 0.75, textAlign: 'left', fontWeight: 600, borderBottom: '2px solid', borderColor: 'divider', whiteSpace: 'nowrap' }} {...props} />
  ),
  td: (props: React.TdHTMLAttributes<HTMLTableCellElement>) => (
    <Box component="td" sx={{ px: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} {...props} />
  ),
  p: (props: React.HTMLAttributes<HTMLParagraphElement>) => (
    <Box component="p" sx={{ m: 0, '& + &': { mt: 0.75 } }} {...props} />
  ),
  strong: (props: React.HTMLAttributes<HTMLElement>) => (
    <Box component="strong" sx={{ fontWeight: 700 }} {...props} />
  ),
};

const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 320;
const MAX_WIDTH = 1200;
const STORAGE_KEY = 'berp_chat_drawer_width';

function formatToolName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function AssistantContent({ text, cursor = false }: { text: string; cursor?: boolean }) {
  return (
    <Box sx={{ fontSize: '0.875rem', lineHeight: 1.5, wordBreak: 'break-word', overflowX: 'auto' }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents as never}>
        {text}
      </ReactMarkdown>
      {cursor && (
        <Box component="span" sx={{ display: 'inline-block', width: 8, height: 14, bgcolor: 'text.secondary', ml: 0.5, animation: 'blink 1s step-end infinite', '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0 } } }} />
      )}
    </Box>
  );
}

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.role === 'user';
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 1.5 }}>
      <Box
        sx={{
          maxWidth: isUser ? '85%' : '100%',
          px: 1.5,
          py: 1,
          borderRadius: isUser ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
          bgcolor: isUser ? 'primary.main' : 'grey.100',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          ...(isUser ? { whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: 1.5, wordBreak: 'break-word' } : {}),
        }}
      >
        {isUser ? msg.content : <AssistantContent text={msg.content} />}
      </Box>
    </Box>
  );
}

function StreamingBubble({ text }: { text: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
      <Box sx={{ maxWidth: '100%', px: 1.5, py: 1, borderRadius: '12px 12px 12px 2px', bgcolor: 'grey.100', color: 'text.primary' }}>
        <AssistantContent text={text} cursor />
      </Box>
    </Box>
  );
}

const CONTEXT_LIMIT = 200_000;

function contextColor(tokens: number): 'success' | 'warning' | 'error' {
  const pct = tokens / CONTEXT_LIMIT;
  if (pct >= 0.8) return 'error';
  if (pct >= 0.5) return 'warning';
  return 'success';
}

export function ChatDrawer() {
  const { isOpen, close, messages, isStreaming, currentReply, activeTools, sessionTokens, contextTokens, credit, sendMessage, abort, clearConversation, compactConversation } = useChat();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [drawerWidth, setDrawerWidth] = useState<number>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return isNaN(parsed) ? DEFAULT_WIDTH : Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, parsed));
  });
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = drawerWidth;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }, [drawerWidth]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = dragStartX.current - e.clientX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidth.current + delta));
      setDrawerWidth(newWidth);
    };
    const onMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setDrawerWidth(w => { localStorage.setItem(STORAGE_KEY, String(w)); return w; });
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentReply, activeTools]);

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text || isStreaming) return;
    setInputValue('');
    sendMessage(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={close}
      variant="temporary"
      PaperProps={{ sx: { width: drawerWidth, display: 'flex', flexDirection: 'column', overflow: 'visible' } }}
    >
      {/* Resize handle */}
      <Box
        onMouseDown={onMouseDown}
        sx={{
          position: 'absolute',
          left: -4,
          top: 0,
          bottom: 0,
          width: 8,
          cursor: 'ew-resize',
          zIndex: 10,
          '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
          transition: 'background-color 0.15s',
        }}
      />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <SmartToyOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
          BERP Assistant
        </Typography>
        <IconButton size="small" onClick={close}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
        {messages.length === 0 && !isStreaming && (
          <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 6 }}>
            <SmartToyOutlinedIcon sx={{ fontSize: 48, mb: 1, color: 'grey.300' }} />
            <Typography variant="body2">
              Ask me anything about your invoices, employees, projects, timesheets, and more.
            </Typography>
          </Box>
        )}

        {messages.map((msg, i) => (
          <MessageBubble key={i} msg={msg} />
        ))}

        {/* Active tool chips */}
        {activeTools.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {activeTools.map(tool => (
              <Chip
                key={tool}
                size="small"
                icon={<CircularProgress size={10} />}
                label={formatToolName(tool)}
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            ))}
          </Box>
        )}

        {/* Streaming text */}
        {isStreaming && currentReply && (
          <StreamingBubble text={currentReply} />
        )}

        {/* Loading indicator (before first text chunk) */}
        {isStreaming && !currentReply && activeTools.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
            <Box sx={{ px: 1.5, py: 1, bgcolor: 'grey.100', borderRadius: '12px 12px 12px 2px' }}>
              <CircularProgress size={16} />
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      <Divider />

      {/* Context panel */}
      {messages.length > 0 && (
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          {/* Context bar + actions row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">
                  Context
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {contextTokens > 0 ? `${Math.round(contextTokens / 1000)}k / ${Math.round(CONTEXT_LIMIT / 1000)}k tokens` : `${Math.floor(messages.length / 2)} exchanges`}
                </Typography>
              </Box>
              <LinearProgress
                variant={contextTokens > 0 ? 'determinate' : 'indeterminate'}
                value={contextTokens > 0 ? Math.min(100, (contextTokens / CONTEXT_LIMIT) * 100) : 0}
                color={contextTokens > 0 ? contextColor(contextTokens) : 'primary'}
                sx={{ height: 3, borderRadius: 1 }}
              />
            </Box>
            <Tooltip title="Compact — summarize and shrink context">
              <span>
                <IconButton size="small" onClick={compactConversation} disabled={isStreaming || messages.length < 2} sx={{ p: 0.5 }}>
                  <CompressIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Clear conversation">
              <span>
                <IconButton size="small" onClick={clearConversation} disabled={isStreaming} sx={{ p: 0.5 }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Session usage + monthly credit */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {(sessionTokens.input > 0 || sessionTokens.output > 0) ? (
              <Typography variant="caption" color="text.disabled">
                Session: {sessionTokens.input.toLocaleString()} in · {sessionTokens.output.toLocaleString()} out
              </Typography>
            ) : <Box />}
            {credit && credit.monthlyTokenLimit > 0 && (
              <Typography
                variant="caption"
                color={credit.remaining === 0 ? 'error' : credit.remaining < credit.monthlyTokenLimit * 0.1 ? 'warning.main' : 'text.disabled'}
              >
                {credit.remaining.toLocaleString()} left this month
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Input */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder="Ask a question..."
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isStreaming}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        {isStreaming ? (
          <IconButton onClick={abort} color="error" size="small" sx={{ mb: 0.5 }}>
            <StopIcon />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleSend}
            color="primary"
            size="small"
            disabled={!inputValue.trim()}
            sx={{ mb: 0.5 }}
          >
            <SendIcon />
          </IconButton>
        )}
      </Box>
    </Drawer>
  );
}
